import { Icon, Image, Typography, VerticalDivider } from '@smartgift/frontend-toolkit';
import dayjs from 'dayjs';
import maxBy from 'lodash/maxBy';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { createSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from 'styled-components';

import { queryClient } from '@hero/clients';
import { DuplicateModal } from '@hero/components/modals';
import { constants } from '@hero/configs';
import {
  ACTIVE_FILTERS_BY_STATUS,
  CARDISLE_GREETING_CARD,
  MY_RECOGNITIONS_TABLE_LIMIT,
  PAYMENT_METHOD_TYPES,
  RECOGNITION_CREATION_STEPS,
  SEND_GIFT_FLOW_STEPS,
  RECOGNITION_STATUSES,
  SEND_GIFT_ROUTE_PATHS,
  FLOW_CODES
} from '@hero/constants';
import {
  useIsAdminOrganization,
  useRecognitionTableFilterOptions,
  useDeleteModalTemplate
} from '@hero/hooks';
import { OptionsDropdown, OrderStatusBadge, Table } from '@hero/molecules';
import { TableProducts } from '@hero/organisms';
import { ROUTE_PATHS } from '@hero/routes';
import {
  recognitionServices,
  useDeleteRecognition,
  useDuplicateRecognition,
  useGetRecognitions,
  useStartRecognition,
  useUpdateRecognition
} from '@hero/services/order-service';
import { useExpireRecognition } from '@hero/services/order-service/recognition/hooks/mutations/useExpireRecognition';
import { useGetUser } from '@hero/services/organization-service/hooks';
import { useNewRecognitionStore, useNotification } from '@hero/store/hooks';
import { formatCurrency, formatDate, formatNumber, getAddonData } from '@hero/utils';

import { Filter } from './Filter';
import { SecondHead } from './SecondHead';
import * as S from './style';
import { reactivateFormValidationSchema } from './validationSchemas';

function getReactivateForm({ isCCPayment }) {
  const recognitionReactivateForm = [
    {
      name: 'expirationDate',
      placeholder: <Trans i18nKey="redemptionDeadline" />,
      type: 'date',
      excludePast: true,
      showTimeSelect: true,
      ...(isCCPayment && { maxDate: dayjs().add(90, 'days').toDate() })
    }
  ];
  return { recognitionReactivateForm };
}

function RecognitionTable({ emptyTableContent }) {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { t } = useTranslation();
  const location = useLocation();

  const { data: user } = useGetUser();

  const {
    updateName,
    updateSettings,
    updateExperienceSettings,
    updateMeta,
    updateContacts,
    updateProducts,
    updateCode,
    updateAddons
  } = useNewRecognitionStore();
  const { mutateAsync: updateRecognition } = useUpdateRecognition();
  const { mutateAsync: duplicateRecognition } = useDuplicateRecognition();
  const { mutateAsync: mutateStartRecognition } = useStartRecognition();
  const { mutateAsync: expireRecognition } = useExpireRecognition();

  const theme = useTheme();
  const recognitionFilterOptions = useRecognitionTableFilterOptions();
  const isOrganizationAdmin = useIsAdminOrganization();
  const { showDeleteModal } = useDeleteModalTemplate({
    title: t('areYouSureYouWantDelete', { type: t('oneTimeGift').toLowerCase() }),
    message: t('deleteRecognitionModalDesc'),
    confirmText: 'DELETE GIFT',
    maxLength: 18
  });
  const { mutateAsync: deleteRecognition } = useDeleteRecognition();

  const [{ pageIndex, pageSize }, setPagination] = useState({
    pageIndex: 0,
    pageSize: MY_RECOGNITIONS_TABLE_LIMIT
  });
  const [globalFilters, setGlobalFilters] = useState({
    searchTerm: '',
    filters: {
      source: [],
      status: [],
      createdBy: ''
    }
  });
  const [duplicateModalData, setDuplicateModalData] = useState({});

  const draftStatuses = [
    RECOGNITION_STATUSES.draft,
    RECOGNITION_STATUSES.pending_approval,
    RECOGNITION_STATUSES.approved
  ];

  const isRecognitionStatusDraft = row => draftStatuses.includes(row.original?.status?.current);

  function getCurrent() {
    return Array.from(
      new Set(
        globalFilters?.filters?.status
          ?.map(f => RECOGNITION_STATUSES[f.value])
          .map(f => {
            if (f === RECOGNITION_STATUSES.readyToSend) return RECOGNITION_STATUSES.draft;
            return f;
          })
      )
    );
  }

  const {
    data: myRecognitions,
    isLoading,
    refetch
  } = useGetRecognitions({
    filters: {
      searchTerm: globalFilters.searchTerm,
      status: {
        current: getCurrent()
      },
      source: Array.from(
        new Set(
          globalFilters?.filters?.source?.map(
            f => constants.apps.find(app => app.id === f.value)?.id
          )
        )
      ),
      meta: {
        creationSteps: globalFilters?.filters?.status?.reduce(
          (r, f) => [...r, ...(ACTIVE_FILTERS_BY_STATUS[f.value] || [])],
          []
        )
      },
      ...(!!globalFilters?.filters?.createdBy &&
        !!isOrganizationAdmin && {
          createdBy: globalFilters?.filters?.createdBy?.map(f => f?.value)?.join(',')
        }),
      ...location?.state
    },
    pagination: {
      page: pageIndex + 1,
      limit: pageSize
    }
  });

  const renderProducts = ({ row }) => {
    return <TableProducts products={row.original?.products} />;
  };

  const onArchiveModal = row => {
    showNotification({
      popupStyle: {
        maxWidth: '500px'
      },
      formStyle: {
        padding: '0 50px'
      },
      title: t('archiveFeaturePromptTitle', { featureType: t('oneTimeGift').toLocaleLowerCase() }),
      message: t('areYouSureWantToArchiveDesc', {
        featureType: t('oneTimeGift').toLocaleLowerCase()
      }),
      notificationImage: <Icon name="error" size="xxl" color="var(--color-error-default)" />,
      primaryButtonProps: { color: 'error', children: t('archive') },
      secondaryButtonProps: { children: t('dontArchive') },
      onSubmit: async () => {
        await updateRecognition({
          params: { recognitionCode: row.original.code },
          body: {
            status: { current: RECOGNITION_STATUSES.archived }
          }
        });
        refetch();
      }
    });
  };

  const onExpireModal = row => {
    showNotification({
      popupStyle: {
        maxWidth: '500px'
      },
      title: t('areYouSure'),
      message:
        row.original?.paymentMethod?.type === PAYMENT_METHOD_TYPES.creditCard ? (
          <Trans
            i18nKey="expireRecognitionPromptMessageCC"
            values={{
              unOpenedCount: row.original.numberOfContacts - row?.original.contacts?.redeemCount
            }}
            components={{ b: <b /> }}
          />
        ) : (
          <Trans
            i18nKey="expireRecognitionPromptMessageHouseAccount"
            values={{
              unOpenedCount: row.original.numberOfContacts - row?.original.contacts?.redeemCount
            }}
            components={{ b: <b /> }}
          />
        ),
      notificationImage: <Icon name="error" size="xxl" color="var(--color-error-default)" />,
      secondaryButtonProps: { children: t('noKeepActive') },
      primaryButtonProps: { color: 'error', children: t('yesEndIt') },
      onSubmit: async () => {
        await expireRecognition({
          code: row.original.code
        });
        refetch();
      }
    });
  };

  const onReactivateModal = async row => {
    const spendingSummary = await queryClient.fetchQuery(
      ['recognitionSpendingSummary', row.original.code],
      () => recognitionServices.spendingSummary(row.original.code)
    );

    const isCCPayment = row.original?.paymentMethod?.type === PAYMENT_METHOD_TYPES.creditCard;
    const { recognitionReactivateForm } = getReactivateForm({ isCCPayment });

    showNotification({
      notification: {
        title: t('reactivateNotificationTitle'),
        message: (
          <Trans
            i18nKey="reactivateNotificationMessage"
            values={{
              totalAmount: formatCurrency(
                spendingSummary.estimatedCharges.total - spendingSummary.spendingSummary.total
              )
            }}
            components={{ b: <b /> }}
          />
        ),
        status: 'info'
      },
      notificationImage: <Icon name="calendar" size="xxl" />,
      header: t('reactivateModalTitleDate'),
      message: t('reactivateModalDescDate'),
      formData: recognitionReactivateForm,
      formValidationSchema: reactivateFormValidationSchema,
      primaryButtonProps: {
        endIcon: 'next',
        children: t('reactivate')
      },
      secondaryButtonProps: { children: t('cancel') },
      onSubmit: async ({ formValues }) => {
        await updateRecognition({
          params: { recognitionCode: row.original.code },
          body: {
            settings: {
              expiresAt: formValues.expirationDate
            },
            status: { current: RECOGNITION_STATUSES.live }
          }
        });

        refetch();
      }
    });
  };

  const onDeleteModal = async row => {
    showDeleteModal({
      onSubmit: async () => {
        await deleteRecognition(row.original.code);
      }
    });
  };

  const onNavigateToDetailPage = async row => {
    if (isRecognitionStatusDraft(row)) {
      if (row.original?.meta?.creationStep !== RECOGNITION_CREATION_STEPS.newRecognitionReviewPay) {
        updateContacts({
          ...row.original.contacts,
          maxCount: row.original?.contacts?.maxCount?.toString() || '0'
        });
        updateName(row.original.name);
        updateCode(row.original.code);
        updateExperienceSettings(row.original.experienceSettings || {});
        updateMeta(row.original.meta);
        updateSettings(row.original.settings);
        updateProducts(row.original?.products);
        updateAddons(row.original?.addons);

        if (
          Object.values(SEND_GIFT_FLOW_STEPS).find(
            step => step === row.original?.meta?.creationStep
          )
        ) {
          navigate(
            ROUTE_PATHS[SEND_GIFT_ROUTE_PATHS[row.original?.meta?.creationStep]](row.original.code)
          );
        } else navigate(ROUTE_PATHS[row.original?.meta?.creationStep]);
      } else {
        navigate(ROUTE_PATHS.NEW_RECOGNITION_REVIEW_PAY(row.original.code));
      }
    } else {
      navigate({
        pathname: ROUTE_PATHS.MY_RECOGNITION_DETAIL(row.original.code),
        search: `?${createSearchParams({
          status: row.original?.status?.current
        })}`
      });
    }
  };

  function calculateAcceptanceRate({ accepted, total } = {}) {
    if (!accepted || !total) return 0;

    return (accepted / total) * 100;
  }

  const hasMultipleApps = constants.apps?.length > 1;
  const columns = React.useMemo(
    () => [
      {
        header: t('name'),
        accessorKey: 'name',
        size: 165,
        cell: ({ getValue, row }) => (
          <Typography
            onClick={() => onNavigateToDetailPage(row)}
            variant="body2"
            as="p"
            lineClamp={1}
            underline
            style={{ cursor: 'pointer' }}
            title={row.original.name}
          >
            {getValue()}
          </Typography>
        )
      },
      ...(isOrganizationAdmin
        ? [
            {
              id: 'sender',
              header: t('sender'),
              cell: ({ cell: { row } }) => {
                return `${row.original.createdBy.firstName} ${row.original.createdBy.lastName}`;
              }
            }
          ]
        : []),
      {
        header: t('gifts'),
        accessorKey: 'products',
        size: 'auto',
        cell: renderProducts
      },
      {
        id: 'contact',
        header: t('recipients'),
        size: 20,
        accessorKey: 'numberOfContacts',
        sortType: 'number'
      },
      {
        id: 'acceptance-rate',
        header: t('redemption'),
        size: 'auto',
        accessorFn: row => calculateAcceptanceRate(row?.acceptanceRates).toFixed(2),
        sortType: 'number',
        cell: ({ getValue }) => (
          <S.AcceptanceRateCell success={getValue() == 100}>{getValue()}%</S.AcceptanceRateCell>
        )
      },
      {
        id: 'expires-date',
        size: 'auto',
        header: t('redemptionDeadline'),
        accessorFn: row => row?.settings?.expiresAt,
        cell: ({ getValue }) =>
          formatDate(getValue(), {
            format: 'MM/DD/YY',
            includeTime: false
          })
      },
      ...(hasMultipleApps
        ? [
            {
              id: 'source',
              size: 'auto',
              header: t('source'),
              cell: ({
                row: {
                  original: { source }
                }
              }) => {
                return (
                  <Image
                    src={theme.images[constants.apps.find(item => item.id === source)?.image]}
                    width="24px"
                    height="24px"
                  />
                );
              }
            }
          ]
        : []),
      {
        header: t('status'),
        accessorKey: 'status',
        cell: ({ getValue, row }) => {
          const value = getValue();
          const isReadyToSend =
            value?.current === RECOGNITION_STATUSES.draft &&
            (row.original?.meta?.creationStep ===
              RECOGNITION_CREATION_STEPS.newRecognitionReviewPay ||
              row.original?.meta?.creationStep === SEND_GIFT_FLOW_STEPS.checkout);

          return (
            <OrderStatusBadge
              status={isReadyToSend ? RECOGNITION_STATUSES.readyToSend : value?.current}
            />
          );
        }
      },
      {
        id: 'actions',
        header: '',
        size: 50,
        cell: ({ row }) => {
          const isDraft =
            row.original.status.current === RECOGNITION_STATUSES.draft &&
            row.original?.meta?.creationStep !== RECOGNITION_CREATION_STEPS.newRecognitionReviewPay;
          const isReadyToSend =
            row.original.status.current === RECOGNITION_STATUSES.draft &&
            row.original?.meta?.creationStep === RECOGNITION_CREATION_STEPS.newRecognitionReviewPay;
          const isLive = row.original.status.current === RECOGNITION_STATUSES.live;
          const isExpired = row.original.status.current === RECOGNITION_STATUSES.expired;
          const isProcessing = row.original.status.current === RECOGNITION_STATUSES.starting;
          const isProcessingError =
            row.original.status.current === RECOGNITION_STATUSES.error_on_start;

          const skus = row.original.products.reduce((result, product) => {
            return [...result, ...product.skus];
          }, []);

          const options = [
            {
              label: t(isDraft ? 'keepEditing' : 'seeDetails'),
              onClick: () => onNavigateToDetailPage(row)
            },
            {
              label: t('duplicate'),
              onClick: () => {
                setDuplicateModalData({
                  isOpen: true,
                  setIsOpen: () => {
                    setDuplicateModalData({
                      isOpen: false
                    });
                  },
                  code: row.original.code,
                  duplicateText: row.original.name,
                  recipientCount: row.original.numberOfContacts,
                  giftCount: row.original.products.length,
                  contactType: row.original.contacts.type,
                  giftMessage: row.original.experienceSettings?.messageParams?.message,
                  giftPrice: maxBy(skus, 'price')?.price,
                  digitalGiftCard: getAddonData(row.original.addons, CARDISLE_GREETING_CARD),
                  meta: row.original.meta,
                  flowCode: row.original.flowCode
                });
              }
            }
          ];

          if (isLive) {
            options.push({
              label: t('endGift'),
              onClick: () => onExpireModal(row)
            });
          }

          if (isExpired) {
            options.push({
              label: t('reactivate'),
              onClick: () => {
                onReactivateModal(row);
              }
            });
            options.push({
              label: t('archive'),
              onClick: () => onArchiveModal(row)
            });
          }

          if (isDraft || isReadyToSend) {
            options.push({
              label: t('delete'),
              color: 'error',
              style: { color: 'var(--color-error-default)' },
              onClick: () => {
                onDeleteModal(row);
              }
            });
          }

          if (isProcessingError) {
            options.splice(1, 1);
          }
          if (isProcessing) {
            options.splice(1, 1);
          }

          if (isProcessingError) {
            options.push({
              label: t('resubmit'),
              onClick: () => mutateStartRecognition(row.original.code)
            });
          }

          return (
            <OptionsDropdown
              popperOptions={{
                placement: 'left',
                modifiers: [
                  {
                    name: 'offset',
                    enabled: true,
                    options: {
                      offset: [50, 0]
                    }
                  }
                ]
              }}
              options={options}
            >
              <Icon name="3dot" px={18} />
            </OptionsDropdown>
          );
        }
      }
    ],
    []
  );

  return (
    <S.Wrapper>
      <Typography variant="body2" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Trans
          i18nKey="whoseRecognition"
          values={{
            firstName: user?.firstName
          }}
          components={{
            b: <b />
          }}
        />
        <VerticalDivider $height="21px" />
        <Trans
          i18nKey="total"
          values={{
            count: formatNumber(myRecognitions?.pagination?.total)
          }}
          components={{
            b: <b />
          }}
        />
      </Typography>
      <Table
        columns={columns}
        data={myRecognitions?.docs || []}
        globalFilters={globalFilters}
        pagination={{
          pageIndex,
          pageSize,
          total: myRecognitions?.pagination?.total
        }}
        filterProps={{
          controlWidth: '150px'
        }}
        filterOptions={recognitionFilterOptions}
        manualPagination
        loading={isLoading}
        setGlobalFilters={getGlobalFilters => {
          const newGlobalFilters = getGlobalFilters(globalFilters);
          setGlobalFilters(newGlobalFilters);
        }}
        setPagination={setPagination}
        paginationProps={{ perPageSelection: true }}
        pageCount={myRecognitions?.pagination?.pages}
        enabled={{ filter: true, header: true }}
        emptyTableContent={emptyTableContent}
        components={{
          SecondHead,
          Filter
        }}
      />
      {duplicateModalData?.isOpen && (
        <DuplicateModal
          isOpen={duplicateModalData.isOpen}
          onOpen={duplicateModalData.setIsOpen}
          onSubmit={async formValues => {
            const response = await duplicateRecognition({
              code: duplicateModalData.code,
              body: formValues
            });

            if (duplicateModalData.flowCode === FLOW_CODES.simplified) {
              navigate(
                ROUTE_PATHS[SEND_GIFT_ROUTE_PATHS[duplicateModalData?.meta?.creationStep]](
                  response.code
                )
              );
            }

            navigate(ROUTE_PATHS[duplicateModalData.meta.creationStep](response.code));
          }}
          featureType="recognition"
          duplicateText={duplicateModalData.duplicateText}
          recipientCount={duplicateModalData.recipientCount}
          giftCount={duplicateModalData.giftCount}
          contactType={duplicateModalData.contactType}
          giftMessage={duplicateModalData.giftMessage}
          giftPrice={duplicateModalData.giftPrice}
          digitalGiftCard={duplicateModalData.digitalGiftCard}
        />
      )}
    </S.Wrapper>
  );
}

export { RecognitionTable };
